import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { Filter, X, Search, Calendar, DollarSign, CreditCard, Banknote, Download, Loader2 } from 'lucide-react'
import { format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { ManualPaymentMethodEnum } from '@/gen/cortado/v1/settings_pb'
import { formatPaymentMethod, useExportInvoicePayments, type PaymentFilterOptions } from '@/stores/payments.store'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'

interface PaymentFilterToolbarProps {
  filters: PaymentFilterOptions
  onFiltersChange: (filters: PaymentFilterOptions) => void
  invoiceId: string
  className?: string
}

export function PaymentFilterToolbar({
  filters,
  onFiltersChange,
  invoiceId,
  className,
}: PaymentFilterToolbarProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [dateFromOpen, setDateFromOpen] = React.useState(false)
  const [dateToOpen, setDateToOpen] = React.useState(false)
  const { exportPayments, isExporting } = useExportInvoicePayments(invoiceId)

  const handleExport = async () => {
    try {
      const result = await exportPayments(filters)
      toast.success(`Exported ${result.rowCount} payment${result.rowCount !== 1 ? 's' : ''} to CSV`)
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), { context: 'payment-filter-export' })
      toast.error('Failed to export payments', {
        description: getErrorDescription(err),
      })
    }
  }

  // Count active filters
  const activeFilterCount = React.useMemo(() => {
    let count = 0
    if (filters.dateFrom) count++
    if (filters.dateTo) count++
    if (filters.amountMinCents) count++
    if (filters.amountMaxCents) count++
    if (filters.stripeOnly) count++
    if (filters.manualOnly) count++
    if (filters.manualMethods?.length) count++
    if (filters.referenceSearch) count++
    if (filters.includeVoided) count++
    return count
  }, [filters])

  const hasFilters = activeFilterCount > 0

  const handleClearFilters = () => {
    onFiltersChange({})
  }

  const updateFilter = <K extends keyof PaymentFilterOptions>(
    key: K,
    value: PaymentFilterOptions[K]
  ) => {
    const newFilters = { ...filters }
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[key]
    } else {
      newFilters[key] = value
    }
    onFiltersChange(newFilters)
  }

  const toggleManualMethod = (method: ManualPaymentMethodEnum) => {
    const current = filters.manualMethods || []
    const newMethods = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method]
    updateFilter('manualMethods', newMethods.length > 0 ? newMethods : undefined)
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Search by reference */}
      <div className="relative flex-1 max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by reference..."
          value={filters.referenceSearch || ''}
          onChange={(e) => updateFilter('referenceSearch', e.target.value || undefined)}
          className="pl-9 h-8"
        />
      </div>

      {/* Filter popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-8 gap-1.5',
              hasFilters && 'border-primary bg-primary/5'
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {hasFilters && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Filter Payments</h4>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-7 text-xs"
                >
                  Clear all
                </Button>
              )}
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Date Range
              </Label>
              <div className="flex items-center gap-2">
                <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex-1 h-8 justify-start text-left font-normal',
                        !filters.dateFrom && 'text-muted-foreground'
                      )}
                    >
                      {filters.dateFrom ? format(filters.dateFrom, 'MMM d, yyyy') : 'From'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.dateFrom}
                      onSelect={(date) => {
                        updateFilter('dateFrom', date)
                        setDateFromOpen(false)
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-xs text-muted-foreground">to</span>
                <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex-1 h-8 justify-start text-left font-normal',
                        !filters.dateTo && 'text-muted-foreground'
                      )}
                    >
                      {filters.dateTo ? format(filters.dateTo, 'MMM d, yyyy') : 'To'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.dateTo}
                      onSelect={(date) => {
                        updateFilter('dateTo', date)
                        setDateToOpen(false)
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Amount Range */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <DollarSign className="h-3 w-3" />
                Amount Range
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.amountMinCents ? (filters.amountMinCents / 100).toString() : ''}
                  onChange={(e) => {
                    const val = e.target.value
                    updateFilter('amountMinCents', val ? Math.round(parseFloat(val) * 100) : undefined)
                  }}
                  className="h-8"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.amountMaxCents ? (filters.amountMaxCents / 100).toString() : ''}
                  onChange={(e) => {
                    const val = e.target.value
                    updateFilter('amountMaxCents', val ? Math.round(parseFloat(val) * 100) : undefined)
                  }}
                  className="h-8"
                />
              </div>
            </div>

            {/* Payment Source */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Payment Source</Label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={filters.stripeOnly}
                    onCheckedChange={(checked) => {
                      updateFilter('stripeOnly', checked ? true : undefined)
                      if (checked) updateFilter('manualOnly', undefined)
                    }}
                  />
                  <span className="text-sm flex items-center gap-1">
                    <CreditCard className="h-3.5 w-3.5" />
                    Stripe only
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={filters.manualOnly}
                    onCheckedChange={(checked) => {
                      updateFilter('manualOnly', checked ? true : undefined)
                      if (checked) updateFilter('stripeOnly', undefined)
                    }}
                  />
                  <span className="text-sm flex items-center gap-1">
                    <Banknote className="h-3.5 w-3.5" />
                    Manual only
                  </span>
                </label>
              </div>
            </div>

            {/* Manual Payment Methods (only show when manual only or no filter) */}
            {(filters.manualOnly || (!filters.stripeOnly && !filters.manualOnly)) && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Manual Payment Methods</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CHECK,
                    ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_WIRE_TRANSFER,
                    ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_ACH,
                    ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CASH,
                  ].map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={filters.manualMethods?.includes(method)}
                        onCheckedChange={() => toggleManualMethod(method)}
                      />
                      <span className="text-sm">{formatPaymentMethod(method)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Include Voided */}
            <div className="pt-2 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={filters.includeVoided}
                  onCheckedChange={(checked) => updateFilter('includeVoided', checked ? true : undefined)}
                />
                <span className="text-sm">Include voided payments</span>
              </label>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear button */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="h-8 px-2"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {/* Export button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={isExporting}
        className="h-8 gap-1.5"
      >
        {isExporting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        Export CSV
      </Button>
    </div>
  )
}
